import {
  AGE_CATEGORIES,
  MORPHOLOGICAL_LABEL,
  VETERAN_CATEGORIES,
  WEIGHT_LIMITS,
} from "../data/judoCategoryRules";

import {
  parseSeasonStartYear,
} from "./seasonUtils";

export const normalizeWeight = (value) => {
  if (
    value === undefined ||
    value === null ||
    value === ""
  ) {
    return null;
  }

  const normalized = Number(
    String(value).replace(",", ".")
  );

  return Number.isFinite(normalized)
    ? normalized
    : null;
};

export const getBirthYearFromDate = (birthDate) => {
  if (!birthDate) {
    return null;
  }

  /*
   * Un input type="date" fournit déjà YYYY-MM-DD.
   * On évite volontairement new Date(...).toISOString(),
   * qui est inutile ici.
   */
  const year = Number.parseInt(
    String(birthDate).slice(0, 4),
    10
  );

  return Number.isFinite(year) ? year : null;
};

export const getReferenceAge = (
  birthYear,
  season
) => {
  const referenceYear =
    parseSeasonStartYear(season);

  return referenceYear - birthYear;
};

export const getAgeCategory = ({
  birthYear,
  season,
}) => {
  const age = getReferenceAge(
    birthYear,
    season
  );

  return (
    AGE_CATEGORIES.find(
      (category) =>
        age >= category.minAge &&
        age <= category.maxAge
    ) || null
  );
};

export const getVeteranCategory = ({
  birthYear,
  season,
  gender,
}) => {
  const age = getReferenceAge(
    birthYear,
    season
  );

  const veteranBand =
    VETERAN_CATEGORIES.find(
      (category) =>
        age >= category.minAge &&
        age <= category.maxAge
    );

  if (!veteranBand) {
    return null;
  }

  const prefix = gender === "F" ? "F" : "M";

  return {
    ...veteranBand,
    key: `${prefix}${veteranBand.index}`,
    label: `Vétéran ${prefix}${veteranBand.index}`,
  };
};

export const formatBirthYears = ({
  minAge,
  maxAge,
  season,
}) => {
  const referenceYear =
    parseSeasonStartYear(season);

  if (maxAge === Infinity) {
    return `${referenceYear - minAge} et avant`;
  }

  const oldestBirthYear =
    referenceYear - maxAge;

  const newestBirthYear =
    referenceYear - minAge;

  if (oldestBirthYear === newestBirthYear) {
    return String(oldestBirthYear);
  }

  return `${oldestBirthYear} – ${newestBirthYear}`;
};

export const getWeightLimits = (
  weightRule,
  gender
) =>
  WEIGHT_LIMITS[weightRule]?.[gender] || [];

export const formatWeightCategories = (
  weightRule,
  gender
) => {
  if (weightRule === "MORPHOLOGICAL") {
    return [MORPHOLOGICAL_LABEL];
  }

  const limits = getWeightLimits(
    weightRule,
    gender
  );

  if (!limits.length) {
    return [];
  }

  return [
    ...limits.map((limit) => `-${limit} kg`),
    `+${limits[limits.length - 1]} kg`,
  ];
};

export const calculateWeightCategory = ({
  weight,
  weightRule,
  gender,
}) => {
  if (weightRule === "MORPHOLOGICAL") {
    return {
      label: MORPHOLOGICAL_LABEL,
      type: "morphological",
      upperLimit: null,
      differenceToLimit: null,
    };
  }

  const normalizedWeight =
    normalizeWeight(weight);

  if (normalizedWeight === null) {
    return null;
  }

  const limits = getWeightLimits(
    weightRule,
    gender
  );

  if (!limits.length) {
    return null;
  }

  const upperLimit = limits.find(
    (limit) => normalizedWeight <= limit
  );

  if (upperLimit !== undefined) {
    return {
      label: `-${upperLimit} kg`,
      type: "weight",
      upperLimit,
      differenceToLimit: Number(
        (upperLimit - normalizedWeight).toFixed(1)
      ),
    };
  }

  const highestLimit =
    limits[limits.length - 1];

  return {
    label: `+${highestLimit} kg`,
    type: "weight",
    upperLimit: null,
    differenceToLimit: null,
  };
};

export const calculateJudoCategories = ({
  birthDate,
  season,
  gender,
  weight,
}) => {
  const birthYear =
    getBirthYearFromDate(birthDate);

  if (!birthYear) {
    throw new Error(
      "La date de naissance est obligatoire."
    );
  }

  const referenceYear =
    parseSeasonStartYear(season);

  if (birthYear > referenceYear) {
    throw new Error(
      "La date de naissance ne peut pas être postérieure à la saison sélectionnée."
    );
  }

  const referenceAge =
    getReferenceAge(birthYear, season);

  const ageCategory = getAgeCategory({
    birthYear,
    season,
  });

  if (!ageCategory) {
    throw new Error(
      "Aucune catégorie fédérale n’a été trouvée pour cette année de naissance."
    );
  }

  const veteranCategory =
    getVeteranCategory({
      birthYear,
      season,
      gender,
    });

  const weightResult =
    calculateWeightCategory({
      weight,
      weightRule:
        ageCategory.weightRule,
      gender,
    });

  return {
    season,
    referenceYear,
    birthYear,
    referenceAge,

    gender,

    ageCategoryKey: ageCategory.key,
    ageCategoryLabel:
      ageCategory.labels[gender],
    ageCategoryBirthYears:
      formatBirthYears({
        ...ageCategory,
        season,
      }),

    weightResult,

    veteranCategory,

    availableWeightCategories:
      formatWeightCategories(
        ageCategory.weightRule,
        gender
      ),
  };
};

export const buildStandardReferenceRows = (
  season
) =>
  AGE_CATEGORIES.flatMap((category) =>
    ["M", "F"].map((gender) => ({
      id: `${category.key}-${gender}`,

      type: "standard",

      categoryKey: category.key,
      categoryLabel:
        category.labels[gender],

      gender,

      minAge: category.minAge,
      maxAge: category.maxAge,

      birthYears: formatBirthYears({
        ...category,
        season,
      }),

      weightCategories:
        formatWeightCategories(
          category.weightRule,
          gender
        ),

      accent: category.accent,
    }))
  );

export const buildVeteranReferenceRows = (
  season
) =>
  VETERAN_CATEGORIES.flatMap(
    (veteran) =>
      ["M", "F"].map((gender) => {
        const prefix =
          gender === "F" ? "F" : "M";

        return {
          id: `VETERAN-${prefix}${veteran.index}`,

          type: "veteran",

          categoryKey: `${prefix}${veteran.index}`,
          categoryLabel: `Vétéran ${prefix}${veteran.index}`,

          gender,

          minAge: veteran.minAge,
          maxAge: veteran.maxAge,

          birthYears: formatBirthYears({
            ...veteran,
            season,
          }),

          weightCategories:
            formatWeightCategories(
              "SENIOR",
              gender
            ),

          accent: "slate",
        };
      })
  );

export const rowContainsBirthYear = (
  row,
  birthYear,
  season
) => {
  if (!birthYear) {
    return true;
  }

  const referenceYear =
    parseSeasonStartYear(season);

  const age =
    referenceYear - Number(birthYear);

  return (
    age >= row.minAge &&
    age <= row.maxAge
  );
};